/**
 * Filter option counts for SO/PO desks — plan/35 C.
 * Hold other filters; free one axis (classic facet histogram).
 */
import { matchesDeliverAxisFilter, matchesDueFilter, matchesLifecycleFilter, matchesPayAxisFilter, matchesReceiveAxisFilter, primaryDueBadge, } from "./due.js";
import { matchesOrderListQuery } from "./order-list.js";
const LIFECYCLE_KEYS = ["draft", "open", "void", "done"];
const PAY_KEYS = ["unpaid", "partial", "paid"];
const DELIVER_KEYS = ["undelivered", "partial", "delivered"];
const RECEIVE_KEYS = ["none", "partial", "received"];
const DUE_KEYS = ["this_week", "overdue", "none"];
function emptyCounts(keys) {
    const out = { all: 0 };
    for (const k of keys)
        out[k] = 0;
    return out;
}
function baseSoMatch(o, filters, opts) {
    if (o.deleted)
        return false;
    if (filters.partyId && o.customerId !== filters.partyId)
        return false;
    if (!matchesOrderListQuery(filters.q ?? "", {
        number: o.number,
        partyName: o.customerName,
        externalRef: o.customerRef,
    })) {
        return false;
    }
    const lifecycle = opts.lifecycle ?? filters.lifecycle;
    const pay = opts.pay ?? filters.pay ?? "all";
    const deliver = opts.deliver ?? filters.deliver ?? "all";
    const due = opts.due ?? filters.due ?? "all";
    if (!matchesLifecycleFilter(o.lifecycle, lifecycle, {
        pay: o.pay,
        deliver: o.deliver,
    })) {
        return false;
    }
    if (!matchesPayAxisFilter(o.pay, pay))
        return false;
    if (!matchesDeliverAxisFilter(o.deliver, deliver))
        return false;
    return matchesDueFilter(primaryDueBadge({ paymentDueOn: o.paymentDueOn, deliverBy: o.deliverBy }, { now: filters.now, timeZone: filters.timeZone }), due);
}
function basePoMatch(o, filters, opts) {
    if (o.deleted)
        return false;
    if (filters.partyId && o.supplierId !== filters.partyId)
        return false;
    if (!matchesOrderListQuery(filters.q ?? "", {
        number: o.number,
        partyName: o.supplierName,
        externalRef: o.supplierRef,
    })) {
        return false;
    }
    const lifecycle = opts.lifecycle ?? filters.lifecycle;
    const pay = opts.pay ?? filters.pay ?? "all";
    const receive = opts.receive ?? filters.receive ?? "all";
    const due = opts.due ?? filters.due ?? "all";
    if (!matchesLifecycleFilter(o.lifecycle, lifecycle, {
        pay: o.pay,
        receive: o.receive,
    })) {
        return false;
    }
    if (!matchesPayAxisFilter(o.pay, pay))
        return false;
    if (!matchesReceiveAxisFilter(o.receive, receive))
        return false;
    return matchesDueFilter(primaryDueBadge({ paymentDueOn: o.paymentDueOn, expectedOn: o.expectedOn }, { now: filters.now, timeZone: filters.timeZone }), due);
}
/** Facet histogram for sales orders — free one axis at a time. */
export function computeSoFacets(rows, filters) {
    const lifecycle = emptyCounts(LIFECYCLE_KEYS);
    const pay = emptyCounts(PAY_KEYS);
    const fulfill = emptyCounts(DELIVER_KEYS);
    const due = emptyCounts(DUE_KEYS);
    for (const key of LIFECYCLE_KEYS) {
        let n = 0;
        for (const o of rows) {
            if (baseSoMatch(o, filters, { lifecycle: key }))
                n += 1;
        }
        lifecycle[key] = n;
    }
    lifecycle.all = rows.filter((o) => baseSoMatch(o, filters, { lifecycle: "all" })).length;
    for (const key of PAY_KEYS) {
        let n = 0;
        for (const o of rows) {
            if (baseSoMatch(o, filters, { pay: key }))
                n += 1;
        }
        pay[key] = n;
    }
    pay.all = rows.filter((o) => baseSoMatch(o, filters, { pay: "all" })).length;
    for (const key of DELIVER_KEYS) {
        let n = 0;
        for (const o of rows) {
            if (baseSoMatch(o, filters, { deliver: key }))
                n += 1;
        }
        fulfill[key] = n;
    }
    fulfill.all = rows.filter((o) => baseSoMatch(o, filters, { deliver: "all" })).length;
    for (const key of DUE_KEYS) {
        let n = 0;
        for (const o of rows) {
            if (baseSoMatch(o, filters, { due: key }))
                n += 1;
        }
        due[key] = n;
    }
    due.all = rows.filter((o) => baseSoMatch(o, filters, { due: "all" })).length;
    return { lifecycle, pay, fulfill, due };
}
/** Facet histogram for purchase orders. */
export function computePoFacets(rows, filters) {
    const lifecycle = emptyCounts(LIFECYCLE_KEYS);
    const pay = emptyCounts(PAY_KEYS);
    const fulfill = emptyCounts(RECEIVE_KEYS);
    const due = emptyCounts(DUE_KEYS);
    for (const key of LIFECYCLE_KEYS) {
        let n = 0;
        for (const o of rows) {
            if (basePoMatch(o, filters, { lifecycle: key }))
                n += 1;
        }
        lifecycle[key] = n;
    }
    lifecycle.all = rows.filter((o) => basePoMatch(o, filters, { lifecycle: "all" })).length;
    for (const key of PAY_KEYS) {
        let n = 0;
        for (const o of rows) {
            if (basePoMatch(o, filters, { pay: key }))
                n += 1;
        }
        pay[key] = n;
    }
    pay.all = rows.filter((o) => basePoMatch(o, filters, { pay: "all" })).length;
    for (const key of RECEIVE_KEYS) {
        let n = 0;
        for (const o of rows) {
            if (basePoMatch(o, filters, { receive: key }))
                n += 1;
        }
        fulfill[key] = n;
    }
    fulfill.all = rows.filter((o) => basePoMatch(o, filters, { receive: "all" })).length;
    for (const key of DUE_KEYS) {
        let n = 0;
        for (const o of rows) {
            if (basePoMatch(o, filters, { due: key }))
                n += 1;
        }
        due[key] = n;
    }
    due.all = rows.filter((o) => basePoMatch(o, filters, { due: "all" })).length;
    return { lifecycle, pay, fulfill, due };
}
/** Label helper: `Unpaid (12)` — omit count when unknown. */
export function facetOptionLabel(base, count) {
    if (count == null || !Number.isFinite(count))
        return base;
    return `${base} (${count})`;
}
